/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — Peak Hunter (PH) heliguide booking wizard:
 *              intro -> data -> e-signature (Yousign) -> payment
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        contact_id, project_id
 *   key:       steps/stepSelected, loadDataTask, saveStepTask,
 *              reqAvanzatiTask, createYouSignTask, createPaymentTask,
 *              nextStepTask, checkFields(), copiaCampiLocationAddress(),
 *              recreateTextAddressTask()
 *   uses:      beebee-base/lib/utility (beebeeAjaxGet/Post, escapeHtml,
 *              notifyUserError), beebee-base/lib/utility/load-script
 *              (loadScript, loads the external Yousign iframe SDK),
 *              beebee-base/lib/get-config, services: notify/router/intl/
 *              session/store/log, applicationController, global
 *              window.Yousign
 *   siblings:  routes/heliguides-personal-data.js (re-exports
 *              routes/guide-personal-data.js), templates/heliguides-personal-data.hbs
 *   tests:     save-refused-test.js, guide-heli-controllers-test.js,
 *              wave5-coverage-test.js, wave9-coverage-test.js,
 *              wave14-coverage-test.js, wave16-coverage-test.js,
 *              wave17-coverage-test.js
 *   gotchas:   nextStepTask must check `saved === false` from saveStepTask —
 *              a settled task whose POST failed still resolves `undefined`
 *              (the success path also resolves undefined), so only the
 *              explicit `false` return means "not stored." Falling through
 *              on a swallowed failure would fire the e-signature/payment step
 *              — or mark the step completed — against data the server never
 *              took.
 *
 * Drives the Peak Hunter heliguide booking wizard: loads the PH timeline,
 * validates participant/principal fields, posts each step, launches the
 * Yousign e-signature flow, and creates the payment session.
 */
import Controller from '@ember/controller';
import { beebeeAjaxPost, beebeeAjaxGet, escapeHtml } from 'beebee-base/lib/utility';
import { notifyUserError } from 'beebee-base/lib/utility';
import { loadScript } from 'beebee-base/lib/utility/load-script';

import { action, computed, set } from '@ember/object';

import { task, timeout } from 'ember-concurrency';

import { inject as iService } from '@ember/service';

import { inject as iController } from '@ember/controller';

import getConfig from 'beebee-base/lib/get-config';
export default class HeliguidePersonalDataController extends Controller {
  @iService notify;
  queryParams = ['contact_id', 'project_id'];
  @iService router;
  @iService intl;
  @iService session;
  @iService store;
  @iService log;
  @iController('application') applicationController;

  steps = [
    {
      name: 'heliguide.intro',
      number: 1,
      status: 'active',
    },
    {
      name: 'heliguide.data',
      number: 2,
      status: '',
    },
    {
      name: 'heliguide.signature',
      number: 3,
      status: '',
    },
    {
      name: 'heliguide.payment',
      number: 4,
      status: '',
    },
  ];

  @computed('steps.@each.status')
  get stepSelected() {
    return this.steps.find((i) => i.status === 'active');
  }

  @(task(function* () {
    yield timeout(250);

    if (!this.session.token || !this.project_id) {
      return;
    }

    try {
      const PHCustomData = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/peakshunter_timeline/${this.project_id}`,
        this.session.token
      );

      const langMap = {
        francese: 'fr-fr',
        inglese: 'en-gb',
        italiano: 'it-it',
      };

      let lang = PHCustomData?.prenotazione?.lang;

      if (lang && langMap[lang]) {
        this.applicationController.switchLangTask.perform(langMap[lang]);
      }

      this.setPHData(PHCustomData);

      this.applicationController.setProperties({
        'content.open_organization': this.store.queryRecord('organization', 'open'),
      });
    } catch (e) {
      this.log.warn('loadDataTask: load failed', e);
    }
  }).restartable())
  loadDataTask;

  setPHData(PHCustomData) {
    /*if(!PHCustomData.utente_principale) {
        PHCustomData.partecipanti = PHCustomData.partecipanti.filter(item => item.id === PHCustomData.loggato);
        PHCustomData.principale = [];
    }*/

    set(this, 'PHCustomData', PHCustomData);
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  recreateTextAddressTask(p) {
    let {
      location_address_street,
      location_address_house,
      location_address_postal_code,
      location_address_city,
      location_address_country,
    } = p;

    let newAddressText = `${location_address_street ?? ''} ${location_address_house ?? ''}<br/>${location_address_postal_code ?? ''} ${location_address_city ?? ''}<br/>${location_address_country ?? ''}`;
    return newAddressText;
  }

  checkFields() {
    var self = this;

    const requiredFieldsPartecipanti = []; //['last_name', 'first_name', 'mail', 'mobile'];
    const requiredFieldsPrincipale = [
      'last_name',
      'first_name',
      'mail',
      'mobile',
      'data_nascita',
      'luogo_nascita',
      'location_address_street',
      'location_address_house',
      'location_address_postal_code',
      'location_address_city',
      'location_address_country',
      'location_address_country_code',
    ];

    let foundEmpty = false;

    (this.PHCustomData.partecipanti || []).forEach((partecipante) => {
      set(partecipante, 'requiredEmpty', {});

      requiredFieldsPartecipanti.forEach((field) => {
        if (!partecipante[field] || partecipante[field].trim() === '') {
          set(partecipante.requiredEmpty, field, true);
          foundEmpty = true;
        }
        if (field === 'mail' && !self.isValidEmail(partecipante[field])) {
          set(partecipante.requiredEmpty, field, true);
          foundEmpty = true;
        }

        if (field === 'mobile' && !partecipante['isMobileValid']) {
          set(partecipante.requiredEmpty, field, true);
          foundEmpty = true;
        }
      });

      // location_address_country holds the country name ('Italia'); the ISO
      // code lives in location_address_country_code. Check both.
      const country = (partecipante.location_address_country || '').toLowerCase();
      const countryCode = (partecipante.location_address_country_code || '').toUpperCase();
      if (
        (country === 'italia' || country === 'italy' || ['IT', 'ITA'].includes(countryCode)) &&
        (!partecipante.cod_fiscale || partecipante.cod_fiscale.trim() === '')
      ) {
        set(partecipante.requiredEmpty, 'cod_fiscale', true);
        foundEmpty = true;
      }
    });

    (this.PHCustomData.principale || []).forEach((principale) => {
      set(principale, 'requiredEmpty', {});

      requiredFieldsPrincipale.forEach((field) => {
        if (!principale[field] || principale[field].trim() === '') {
          if (field.includes('location_')) {
            set(principale.requiredEmpty, 'address', true);
          } else {
            set(principale.requiredEmpty, field, true);
          }
          foundEmpty = true;
        }

        if (field === 'mail' && !self.isValidEmail(principale[field])) {
          set(principale.requiredEmpty, field, true);
          foundEmpty = true;
        }

        if (field === 'mobile' && !principale['isMobileValid']) {
          set(principale.requiredEmpty, field, true);
          foundEmpty = true;
        }

        set(principale, 'location_address_text', self.recreateTextAddressTask(principale));
      });
    });

    return foundEmpty;
  }

  copiaCampiLocationAddress(principale, partecipante) {
    if (!principale || !partecipante) {
      return;
    }

    const indexPartecipante = partecipante.findIndex(
      (item) => item.address_id === principale.address_id
    );

    if (indexPartecipante !== -1) {
      // Copia i campi location_address_* dal principale al partecipante
      partecipante[indexPartecipante].location_address_text = principale.location_address_text;
      partecipante[indexPartecipante].location_address_street = principale.location_address_street;
      partecipante[indexPartecipante].location_address_house = principale.location_address_house;
      partecipante[indexPartecipante].location_address_postal_code =
        principale.location_address_postal_code;
      partecipante[indexPartecipante].location_address_city = principale.location_address_city;
      partecipante[indexPartecipante].location_address_country =
        principale.location_address_country;
      partecipante[indexPartecipante].location_address_country_code =
        principale.location_address_country_code;
    }

    return partecipante;
  }

  @(task(function* (stepNumber) {
    yield timeout(250);

    if (!this.PHCustomData?.principale) {
      this.log.warn('[heliguides-personal-data] saveStepTask: PHCustomData.principale not available');
      return;
    }

    for (let i = 0; i < this.PHCustomData.principale.length; i++) {
      this.copiaCampiLocationAddress(
        this.PHCustomData.principale[i],
        this.PHCustomData.partecipanti
      );
    }

    try {
      yield beebeeAjaxPost(`${getConfig(this).APP.urlApi}/peakshunter_booking_step`, this.session.token, {
        timeline: this.PHCustomData,
        step: stepNumber,
      });
    } catch (e) {
      this.log.error('saveStepTask failed', e);
      notifyUserError(this.notify, e, this.intl);
      // Report the refusal to the caller: a settled task is not a stored step.
      return false;
    }
  }).restartable())
  saveStepTask;

  @(task(function* (p) {
    yield timeout(250);

    try {
      const PHCustomData = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/peakshunter_send_request`,
        this.session.token,
        {
          contact_id: p.id,
          project_id: this.project_id,
          mail: p.mail,
          product_name: this.PHCustomData?.prenotazione?.product_name,
          contact_name: `${p.first_name} ${p.last_name}`,
        }
      );

      this.setPHData(PHCustomData);
      this.notify.success('Richiesta inviata');
    } catch (e) {
      this.notify.error('Impossibile inviare la richiesta');
    }
  }).restartable())
  reqAvanzatiTask;

  @(task(function* (onlyUrl = true) {
    yield timeout(250);

    const tlFirma = this.PHCustomData.timeline.find((i) => i.name === 'Contratto Firmato');

    if (tlFirma?.stats.completed) {
      this.nextStepTask.perform('next');
      return;
    }

    if (!window.Yousign) {
      try {
        yield loadScript(`https://cdn.yousign.tech/iframe-sdk-1.4.0.min.js`);
      } catch (e) {
        this.log.warn('[heliguides-personal-data] Yousign SDK load failed:', e?.status, e?.statusText);
      }
    }

    set(this, 'contractFileUrl', null);

    const principale = this.PHCustomData.principale[0] || {};
    const { id, first_name, last_name, mobile, mail } = principale;
    const idModulo = 1006;
    const lang = this.PHCustomData?.prenotazione?.lang || 'italiano';
    const doclang =
      lang === 'tedesco' ? 'DE' : lang === 'italiano' ? 'ITA' : lang === 'francese' ? 'FR' : 'EN';
    const cId = id;
    const prId = this.PHCustomData.prenotazione.id;
    const fileUrl = `https://asset.beebeeboard.com/${this.applicationController.get('content.open_organization.organization')}/moduli/${doclang}_proposta_contratto.php?full=true&project_id=${prId}&token=${this.session.token}&index=${idModulo}`;

    if (onlyUrl) {
      set(this, 'contractFileUrl', fileUrl);
      return;
    }

    try {
      const sendData = {
        organization: this.applicationController.get('content.open_organization.organization'),
        organization_name: this.applicationController.get('content.open_organization.name'),
        logo: this.applicationController.get('content.open_organization.thumbnail_url'),
        color: this.applicationController.get('content.open_organization.primary_color'),
        token: this.session.token,
        consenso_id: idModulo,
        contact_id: cId,
        project_id: prId,
        file_url: fileUrl,
        first_name: first_name,
        last_name: last_name,
        mail: mail,
        message: 'Firma Contratto',
        mobile: mobile,
        name: `Contratto ${first_name} ${last_name}`,
        language: lang,
        signature_level: 'electronic_signature',
        notify_signatory_by_mail: false,
        signature_authentication_mode: 'otp_sms', //'no_otp', //'otp_sms',
        yousign_sandbox: this.session.option_field?.yousign_sandbox || false,
      };

      let sign = yield beebeeAjaxPost(
        `https://asset.beebeeboard.com/utility/yousign/signature_request.php`,
        this.session.token,
        sendData
      );

      if (sign.error) {
        let text = `${escapeHtml(sign.detail?.detail)} <ul>`;
        for (let i = 0; i < (sign.detail?.invalid_params || []).length; i++) {
          text += `<li>${escapeHtml(sign.detail.invalid_params[i].name)}: ${escapeHtml(sign.detail.invalid_params[i].reason)}</li>`;
        }
        text += '</ul>';
        this.notify.warning({
          html: text,
          closeAfter: null,
        });

        this.nextStepTask.perform('back');
      } else if (sign.success) {
        const yousign = new Yousign({
          signatureLink: sign.url,
          iframeContainerId: 'iframe-container',
          isSandbox: this.session.option_field?.yousign_sandbox || false,
        });

        yousign.onSuccess((data) => {
          this.notify.success('Signer has successfully signed');
          this.nextStepTask.perform('next');
          //this.createPaymentTask.perform();
        });

        yousign.onError((data) => {
          this.notify.error('Signer encountered an error when signing');
        });

      }
    } catch (e) {
      this.notify.error('Impossibile generare la richiesta');
    }
  }).restartable())
  createYouSignTask;

  @(task(function* () {
    yield timeout(250);

    try {
      const paymentId = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/guidemonterosa_create_session`,
        this.session.token,
        {
          project_id: this.PHCustomData?.prenotazione?.id,
        }
      );

      if (paymentId?.id) {
        let p = yield this.store.findRecord('payment', paymentId.id, {
          reload: true,
        });

        set(this, 'paymentAPI', p);
      }
    } catch (e) {
      this.notify.error('Impossibile inviare la richiesta');
    }
  }).restartable())
  createPaymentTask;

  @task(function* (direction) {
    const stepActive = this.steps.find((i) => i.status === 'active');

    let foundEmpty = null;
    if (stepActive?.number === 2) {
      foundEmpty = this.checkFields();
    }

    if (foundEmpty) {
      return;
    }

    if (direction === 'next' && stepActive.number > 1) {
      const saved = yield this.saveStepTask.perform(stepActive.number);
      // saveStepTask swallows the POST failure, so a settled task said nothing
      // about whether the participant data was stored. Falling through fired
      // the e-signature request — or created a real payment session — against
      // data the server never took, and marked the step completed.
      // Test `=== false`: the success path resolves undefined.
      if (saved === false) {
        return;
      }
    }

    if (direction === 'next') {
      if (stepActive?.number === 2) {
        yield this.createYouSignTask.perform();
      } else if (stepActive?.number === 3) {
        yield this.createPaymentTask.perform();
      }
    }

    const newIndex = direction === 'next' ? stepActive.number + 1 : stepActive.number - 1;

    for (let i = 0; i < this.steps.length; i++) {
      let step = this.steps[i];
      if (step.number < newIndex) {
        set(step, 'status', 'completed');
      } else if (step.number === newIndex) {
        set(step, 'status', 'active');
      } else {
        set(step, 'status', '');
      }
    }
  })
  nextStepTask;

  @action
  clickLogo() {}
}
